import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  FileText,
  ImageIcon,
  CheckCircle2,
  AlertTriangle,
  Copy,
  Download,
  Lock,
  User,
  Check,
  X as XIcon,
} from "lucide-react";
import { generateMonthlyReportPdf } from "@/lib/monthlyReportPdf";
import { api } from "@/lib/api";

const STATUS_META = {
  processed: { label: "Counted", icon: CheckCircle2, color: "#1D9E75" },
  duplicate: { label: "Duplicate", icon: Copy, color: "#BA7517" },
  unrelated: { label: "Skipped", icon: AlertTriangle, color: "#E24B4A" },
  unreadable: { label: "Unreadable", icon: AlertTriangle, color: "#E24B4A" },
  image_unreadable: { label: "Unreadable", icon: AlertTriangle, color: "#E24B4A" },
  partial_read: { label: "Partial", icon: AlertTriangle, color: "#BA7517" },
  password_protected: { label: "Locked", icon: AlertTriangle, color: "#E24B4A" },
  empty: { label: "Empty", icon: AlertTriangle, color: "#E24B4A" },
};

// "2026-04-12T..." → "2026-04"
const monthIso = (iso) => {
  if (!iso || typeof iso !== "string") return "";
  return iso.length >= 7 ? iso.slice(0, 7) : "";
};

const monthLabelFromIso = (m) => {
  if (!m) return "—";
  const [y, mo] = m.split("-").map((n) => parseInt(n, 10));
  if (!y || !mo) return m;
  return new Date(y, mo - 1, 1).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
};

const shortMonthLabel = (m) => {
  if (!m) return "—";
  const [y, mo] = m.split("-").map((n) => parseInt(n, 10));
  if (!y || !mo) return m;
  return new Date(y, mo - 1, 1).toLocaleDateString("en-IN", {
    month: "short",
    year: "2-digit",
  });
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
};

const todayDDMMYYYY = () => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};

// Short invoice # from filename (strip extension, crop to 28 chars)
const invoiceNumber = (row) => {
  const name = (row.filename || "").replace(/\.(pdf|jpe?g|png|webp)$/i, "");
  return name.length > 28 ? name.slice(0, 26) + "…" : name || row.id?.slice(0, 8) || "—";
};

const TYPE_META = {
  image: { label: "Screenshot", icon: ImageIcon, color: "#1D9E75" },
  pdf: { label: "PDF", icon: FileText, color: "#FF6B00" },
};

const PRO_PLANS = new Set(["pro", "family"]);

/**
 * Upload ledger card with E3 monthly archive — month chips + per-month
 * downloadable PDF (Pro/Family). Free + Starter users see a lock on past
 * months and a single "Upgrade for monthly archive" nudge.
 */
export default function InvoiceHistoryTable({
  invoices,
  currentPlan = "free",
  timeline = [],
  onUpgrade,
  familyMembers = [],
}) {
  // Group invoices by ISO month (e.g. "2026-04"), keyed map preserves order.
  // Then BACKFILL every month from April 26 (CartIQ launch month) up to the
  // current month so the chips strip always shows a continuous timeline,
  // even months where the user uploaded nothing (#9 — feedback request).
  const grouped = useMemo(() => {
    const map = new Map();
    (invoices || []).forEach((row) => {
      const k = monthIso(row.upload_date);
      const key = k || "unknown";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });

    // Backfill empty months from launch (Apr 2026) to today.
    const launch = new Date(2026, 3, 1); // April 2026
    const now = new Date();
    const cursor = new Date(launch.getFullYear(), launch.getMonth(), 1);
    const seen = new Set(map.keys());
    while (
      cursor.getFullYear() < now.getFullYear() ||
      (cursor.getFullYear() === now.getFullYear() &&
        cursor.getMonth() <= now.getMonth())
    ) {
      const yyyy = cursor.getFullYear();
      const mm = String(cursor.getMonth() + 1).padStart(2, "0");
      const key = `${yyyy}-${mm}`;
      if (!seen.has(key)) {
        map.set(key, []); // empty array = "no uploads this month"
        seen.add(key);
      }
      cursor.setMonth(cursor.getMonth() + 1);
    }

    // Sort keys descending so most-recent month appears first.
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [invoices]);

  const total = invoices?.length || 0;
  const processed = (invoices || []).filter(
    (i) => (i.status || "processed") === "processed"
  ).length;

  const [selected, setSelected] = useState("all");

  const isProTier = PRO_PLANS.has((currentPlan || "free").toLowerCase());

  // Latest (current) month is always unlocked. Past months gated for non-Pro.
  const latestMonthKey = grouped[0]?.[0] || "";

  const visible = useMemo(() => {
    if (selected === "all") return grouped;
    return grouped.filter(([m]) => m === selected);
  }, [selected, grouped]);

  const findTimelineRow = (m) =>
    (timeline || []).find((t) => t.month === m) || null;

  const handleDownload = (m, rows) => {
    if (!isProTier) {
      toast.info("Per-month PDF downloads are a Pro feature. Upgrade to unlock.");
      onUpgrade?.();
      return;
    }
    try {
      generateMonthlyReportPdf({
        month: m,
        label: monthLabelFromIso(m),
        rows,
        timelineRow: findTimelineRow(m),
      });
      toast.success(`Downloaded analysis for ${monthLabelFromIso(m)}.`);
    } catch (e) {
      console.error("monthly pdf failed", e);
      toast.error("Could not generate the monthly PDF.");
    }
  };

  const handleChipClick = (m) => {
    if (!isProTier && m !== "all" && m !== latestMonthKey) {
      toast.info("Past-month archives are a Pro feature. Upgrade to time-travel.");
      onUpgrade?.();
      return;
    }
    setSelected(m);
  };

  // Heading uses selected month if any, else latest
  const headingMonth =
    selected !== "all"
      ? monthLabelFromIso(selected)
      : monthLabelFromIso(latestMonthKey) ||
        new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" });

  return (
    <div className="cartiq-card" data-testid="invoice-history-card">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1">
            — Your upload ledger
          </div>
          <h2 className="font-display uppercase text-2xl" data-testid="invoice-history-heading">
            Invoices Uploaded for {headingMonth}
          </h2>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-xs text-[#888] uppercase tracking-wider">
            <span className="font-mono-num text-[#F0EDE8]">{processed}</span>
            <span className="text-[#555]"> / {total}</span> counted
          </div>
          <div
            className="px-2.5 py-1 bg-[#BA7517]/10 border border-[#BA7517]/40 text-[10px] uppercase tracking-[0.22em] text-[#BA7517] font-bold font-mono-num"
            data-testid="invoice-history-today"
            title="Today's date (DD/MM/YYYY)"
          >
            As of {todayDDMMYYYY()}
          </div>
        </div>
      </div>

      {/* E3: Month chips strip — horizontally scrollable on small screens */}
      {grouped.length > 0 && (
        <div
          className="flex items-center gap-2 overflow-x-auto pb-3 mb-4 border-b border-[#2A2A35]"
          data-testid="invoice-history-month-chips"
        >
          <ChipButton
            active={selected === "all"}
            onClick={() => setSelected("all")}
            testid="month-chip-all"
          >
            All months <span className="text-[#555] ml-1">· {total}</span>
          </ChipButton>
          {grouped.map(([m, rows]) => {
            const locked = !isProTier && m !== latestMonthKey;
            return (
              <ChipButton
                key={m}
                active={selected === m}
                onClick={() => handleChipClick(m)}
                locked={locked}
                testid={`month-chip-${m}`}
              >
                {locked && <Lock className="w-3 h-3" />}
                {shortMonthLabel(m)}
                <span className="text-[#555] ml-1">· {rows.length}</span>
              </ChipButton>
            );
          })}
        </div>
      )}

      {total === 0 && (
        <div className="text-sm text-[#888] py-4 text-center">
          No invoices yet — upload your first PDF or screenshot above.
        </div>
      )}

      {visible.map(([month, rows]) => (
        <div key={month} className="mb-5 last:mb-0" data-testid={`invoice-history-month-${month}`}>
          <div className="flex items-center justify-between gap-3 mb-2 border-b border-[#2A2A35] pb-2">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#888] font-bold">
              {monthLabelFromIso(month)}{" "}
              <span className="text-[#555]">
                · {rows.length} {rows.length === 1 ? "invoice" : "invoices"}
              </span>
            </div>
            {rows.length > 0 && (
              <button
                type="button"
                onClick={() => handleDownload(month, rows)}
                className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 border transition-colors ${
                  isProTier
                    ? "border-[#FF6B00] text-[#FF6B00] hover:bg-[#FF6B00] hover:text-white"
                    : "border-[#2A2A35] text-[#555] hover:border-[#FF6B00] hover:text-[#FF6B00]"
                }`}
                data-testid={`download-month-${month}`}
                title={
                  isProTier
                    ? `Download ${monthLabelFromIso(month)} analysis as PDF`
                    : "Pro feature — upgrade to download monthly PDFs"
                }
              >
                {isProTier ? <Download className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                {isProTier ? "Download month" : "Pro · Download"}
              </button>
            )}
          </div>
          {rows.length === 0 ? (
            <div className="text-xs text-[#666] italic px-1 py-3">
              No uploads this month yet.
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full cartiq-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Type</th>
                  <th>Platform</th>
                  <th>Invoice Date</th>
                  <th className="text-right">Items</th>
                  <th className="hidden md:table-cell">Paid by</th>
                  <th className="text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const meta = STATUS_META[row.status || "processed"] || STATUS_META.processed;
                  const Icon = meta.icon;
                  const tMeta = TYPE_META[row.kind === "image" ? "image" : "pdf"];
                  const TIcon = tMeta.icon;
                  return (
                    <tr key={row.id || i} data-testid={`invoice-history-row-${i}`}>
                      <td>
                        <div className="flex items-center gap-2 min-w-0">
                          <TIcon className="w-3.5 h-3.5 shrink-0" style={{ color: tMeta.color }} />
                          <span className="truncate font-bold text-sm">{invoiceNumber(row)}</span>
                        </div>
                      </td>
                      <td>
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold"
                          style={{ color: tMeta.color }}
                          data-testid={`invoice-history-type-${i}`}
                        >
                          <TIcon className="w-3 h-3" />
                          {tMeta.label}
                        </span>
                      </td>
                      <td className="text-[#888] uppercase tracking-wider text-xs">
                        {(() => {
                          // Show "—" for non-purchase rows (unrelated docs,
                          // unreadable PDFs, password-protected, empty) since
                          // those aren't quick-commerce invoices at all and
                          // labelling them with a platform name is misleading.
                          const skipped = new Set([
                            "unrelated",
                            "unreadable",
                            "image_unreadable",
                            "password_protected",
                            "empty",
                          ]);
                          if (skipped.has(row.status)) return "—";
                          // Trim a trailing "OTHER" to "—" too — clearer than
                          // a generic catch-all word in a quick-commerce app.
                          const p = (row.platform || "").trim();
                          if (!p || p.toLowerCase() === "other") return "—";
                          return p;
                        })()}
                      </td>
                      <td className="font-mono-num">{fmtDate(row.upload_date)}</td>
                      <td className="text-right font-mono-num">{row.items_extracted || 0}</td>
                      <td className="hidden md:table-cell">
                        <PaidByCell
                          invoiceId={row.id}
                          initial={row.paid_by}
                          familyMembers={familyMembers}
                          testid={`paid-by-cell-${i}`}
                        />
                      </td>
                      <td className="text-right">
                        <span
                          className="inline-flex items-center gap-1 text-xs uppercase tracking-wider font-bold"
                          style={{ color: meta.color }}
                        >
                          <Icon className="w-3 h-3" />
                          {meta.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
      ))}

      {/* Free/Starter users — single nudge below the chips when past months exist */}
      {!isProTier && grouped.length > 1 && (
        <div
          className="mt-4 p-3 border border-[#FF6B00]/30 bg-[#FF6B00]/5 flex items-center justify-between gap-3 flex-wrap"
          data-testid="monthly-archive-upsell"
        >
          <div className="text-xs text-[#F0EDE8] leading-relaxed">
            <span className="text-[#FF6B00] font-bold">Pro unlocks the full archive</span> — browse
            past months, filter by platform, and download a printable analysis PDF every month.
          </div>
          <button
            type="button"
            onClick={onUpgrade}
            className="text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-2 bg-[#FF6B00] text-white hover:bg-[#FF8500]"
            data-testid="monthly-archive-upgrade-btn"
          >
            Upgrade to Pro
          </button>
        </div>
      )}
    </div>
  );
}

const ChipButton = ({ active, onClick, locked, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`shrink-0 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1.5 border transition-colors ${
      active
        ? "bg-[#FF6B00] text-white border-[#FF6B00]"
        : locked
        ? "border-[#2A2A35] text-[#555] hover:border-[#FF6B00] hover:text-[#FF6B00]"
        : "border-[#2A2A35] text-[#888] hover:border-[#F0EDE8] hover:text-[#F0EDE8]"
    }`}
  >
    {children}
  </button>
);

// ─── Inline "Paid by" attribution cell ──────────────────────────────
//
// Desktop-only. Free-text with a small preset menu when the user is on a
// Family plan (populated with family_members). Persists via PATCH
// /api/upload/{invoice_id}/paid-by. Non-blocking — the row keeps rendering
// even when the save is in-flight.
export function PaidByCell({ invoiceId, initial, familyMembers = [], testid }) {
  const [value, setValue] = useState(initial || "");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const displayValue = value || "You";

  const save = async (next) => {
    setBusy(true);
    try {
      const res = await api.patch(`/upload/${invoiceId}/paid-by`, {
        paid_by: next,
      });
      setValue(res?.data?.paid_by || "");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
        data-testid={testid}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-[#F0EDE8] hover:text-[#FF6B00] transition-colors group"
        title="Click to edit"
      >
        <User className="w-3 h-3 text-[#888] group-hover:text-[#FF6B00]" />
        <span className="truncate max-w-[120px]">{displayValue}</span>
      </button>
    );
  }

  const presets = ["You", "Split equally", ...(familyMembers || []).map((m) => m.name).filter(Boolean)];
  return (
    <div className="flex items-center gap-1" data-testid={`${testid}-editing`}>
      <input
        type="text"
        list={`paid-by-presets-${invoiceId}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, 60))}
        onKeyDown={(e) => {
          if (e.key === "Enter") save(draft.trim());
          if (e.key === "Escape") setEditing(false);
        }}
        placeholder="You"
        disabled={busy}
        autoFocus
        className="bg-[#0F0F14] border border-[#FF6B00] text-[#F0EDE8] text-xs px-2 py-1 w-[140px] focus:outline-none"
        data-testid={`${testid}-input`}
      />
      <datalist id={`paid-by-presets-${invoiceId}`}>
        {presets.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>
      <button
        type="button"
        onClick={() => save(draft.trim())}
        disabled={busy}
        className="p-1 text-[#1D9E75] hover:text-[#F0EDE8] disabled:opacity-40"
        aria-label="Save"
        data-testid={`${testid}-save`}
      >
        <Check className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => setEditing(false)}
        disabled={busy}
        className="p-1 text-[#888] hover:text-[#F0EDE8]"
        aria-label="Cancel"
        data-testid={`${testid}-cancel`}
      >
        <XIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
